import { extractCustomerSlice } from '@/Screens/VerifyBusiness/helpers';
import { isCustomerSliceType } from '@/types/customer.ext';
import { Permission } from '@/types/permissions';
import { copyWith } from '@/utility/copyWith';
import { withNullsRemoved } from '@/utility/withNullsRemoved';

export interface CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType;
}

export interface BusinessAddress {
  readonly streetAddress: string | null;
  readonly complement: string | null;
  readonly state: string | null;
  readonly city: string | null;
  readonly zipCode: number | null;
  readonly country?: string | null;
}

export class BusinessAddress {
  public static empty(): BusinessAddress {
    return {
      complement: null,
      state: null,
      streetAddress: null,
      city: null,
      country: null,
      zipCode: null,
    };
  }
}

export interface Owner {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly share: number | null;
  readonly address: BusinessAddress | null;
  readonly document: string | null;
  readonly sourceOfWealth: WealthSourceType | null;
  readonly occupation: OccupationType | null;
  readonly annualIncome: number | null;
}

export enum CustomerSliceType {
  companyDetails = 'companyDetails',
  businessOperationsProfile = 'businessOperationsProfile',
  businessOperationsCustomers = 'businessOperationsCustomers',
  businessOperationsFundsSource = 'businessOperationsFundsSource',
  userPersonalInfo = 'userPersonalInfo',
  organizationLegalRepresentative = 'organizationLegalRepresentative',
  organizationContact = 'organizationContact',
  socialNetwork = 'socialNetwork',
  organizationOwners = 'organizationOwners',
}

export enum EntityType {
  microEmpreendedorIndividualMEI = 'MICRO_EMPREENDEDOR_INDIVIDUAL_MEI',
  empresaIndividualEI = 'EMPRESA_INDIVIDUAL_EI',
  sociedadeLimitadaUnipessoalSLU = 'SOCIEDADE_LIMITADA_UNIPESSOAL_SLU',
  sociedadeLimitadaEmpresarialLTDA = 'SOCIEDADE_LIMITADA_EMPRESARIAL_LTDA',
  sociedadeSimpleSS = 'SOCIEDADE_SIMPLES_SS',
  sociedadeAnonimaSA = 'SOCIEDADE_ANONIMA_SA',
}

export enum IndustryType {
  energyOilGasFuels = 'ENERGY_OIL_GAS_FUELS',
  materialsCommodities = 'MATERIALS_COMMODITIES',
  manufacturingConstruction = 'MANUFACTURING_CONSTRUCTION',
  professionalServices = 'PROFESSIONAL_SERVICES',
  medicalServices = 'MEDICAL_SERVICES',
  transportation = 'TRANSPORTATION',
  travelLeisure = 'TRAVEL_LEISURE',
  pharmaceuticals = 'PHARMACEUTICALS',
  foodBeverage = 'FOOD_BEVERAGE',
  entertainment = 'ENTERTAINMENT',
  gambling = 'GAMBLING',
  cryptoWeb3 = 'CRYPTO_WEB3',
  banksInsuranceFinancial = 'BANKS_INSURANCE_FINANCIAL',
  itSoftware = 'IT_SOFTWARE',
  telecom = 'TELECOM',
  utilities = 'UTILITIES',
  ageRestricted = 'AGE_RESTRICTED',
  other = 'OTHER',
}

export enum OccupationType {
  executive = 'executive',
  sales = 'sales',
  consulting = 'consulting',
  government = 'government',
  healthcare = 'healthcare',
  financeAccounting = 'financeAccounting',
  technology = 'technology',
  scienceEngineering = 'scienceEngineering',
  foodServices = 'foodServices',
  legalServices = 'legalServices',
  manufacturing = 'manufacturing',
  other = 'other',
}

export enum WealthSourceType {
  salary = 'salary',
  selfEmployed = 'selfEmployed',
  inheritance = 'inheritance',
  familySupport = 'familySupport',
  investments = 'investments',
  retirement = 'retirement',
  rentalIncome = 'rentalIncome',
  governmentBenefits = 'governmentBenefits',
  businessSale = 'businessSale',
  other = 'other',
}

export enum CustomerIndustryType {
  firearms = 'FIREARMS',
  gambling = 'GAMBLING',
  foreignNGOS = 'FOREIGN_NGOS',
  cannabis = 'CANNABIS',
  adult = 'ADULT',
  metals = 'METALS',
  none = 'NONE',
}

export enum BeneficiaryType {
  individual = 'INDIVIDUAL',
  business = 'BUSINESS',
}

export interface CustomerCompanyDetailsPatchRequest extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.companyDetails;

  readonly commercialName: string | null;
  readonly legalEntityName: string | null;
  readonly entityType: EntityType | null;
  readonly taxId: string | null;
  readonly address: BusinessAddress | null;
  readonly operationStartDate: string /* Date (Java java.time.LocalDate) */ | null;
}

export interface CustomerCompanyDetails {
  readonly commercialName: string | null;
  readonly legalEntityName: string | null;
  readonly entityType: EntityType | null;
  readonly taxId: string | null;
  readonly address: BusinessAddress | null;
  readonly operationStartDate: string /* Date (Java java.time.LocalDate) */ | null;
}

export class CustomerCompanyDetails {
  public static fromCustomer(customer: Customer): CustomerCompanyDetails {
    const { businessInfo } = customer;

    return {
      commercialName: businessInfo?.commercialName ?? businessInfo?.name ?? null,
      legalEntityName: businessInfo?.legalEntityName ?? businessInfo?.name ?? null,
      entityType: businessInfo?.entityType ?? null,
      taxId: businessInfo?.taxId ?? null,
      operationStartDate: businessInfo?.operationStartDate ?? null,
      address: businessInfo?.address ?? null,
    };
  }
}

export interface CustomerBusinessOperationsProfilePatchRequest
  extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.businessOperationsProfile;

  readonly industry: IndustryType | null;
  readonly services: string | null;
  readonly countries: readonly string[] | null;
  readonly isFinancialInstitution: boolean | null;
  readonly hasBrazilLicenseRequirement: boolean | null;
}

export interface CustomerBusinessOperationsProfile {
  readonly industry: IndustryType | null;
  readonly subcategories: readonly string[] | null;
  readonly services: string | null;
  readonly countries: readonly string[] | null;
  readonly isFinancialInstitution: boolean | null;
  readonly hasBrazilLicenseRequirement: boolean | null;
}

export class CustomerBusinessOperationsProfile {
  public static fromCustomer(customer: Customer): CustomerBusinessOperationsProfile {
    const { businessInfo } = customer;

    return {
      industry: businessInfo?.operations?.profile?.industry ?? null,
      subcategories: businessInfo?.operations?.profile?.subcategories ?? null,
      services: businessInfo?.operations?.profile?.services ?? null,
      countries: businessInfo?.operations?.profile?.countries ?? null,
      isFinancialInstitution: businessInfo?.operations?.profile?.isFinancialInstitution ?? null,
      hasBrazilLicenseRequirement:
        businessInfo?.operations?.profile?.hasBrazilLicenseRequirement ?? null,
    };
  }
}

export interface CustomerBusinessOperationsCustomersPatchRequest
  extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.businessOperationsCustomers;

  readonly customerType: readonly BeneficiaryType[] | null;
  readonly typicalCustomer: string | null;
  readonly countries: readonly string[] | null;
  readonly quantity: number | null;
  readonly industries?: readonly CustomerIndustryType[] | null;
}

export interface CustomerBusinessOperationsCustomers {
  readonly customerType: readonly BeneficiaryType[] | null;
  readonly typicalCustomer: string | null;
  readonly countries: readonly string[] | null;
  readonly quantity: number | null;
  readonly industries: readonly CustomerIndustryType[] | null;
}

export class CustomerBusinessOperationsCustomers {
  public static fromCustomer(customer: Customer): CustomerBusinessOperationsCustomers {
    const { businessInfo } = customer;

    return {
      customerType: businessInfo?.operations?.customers?.customerType ?? null,
      typicalCustomer: businessInfo?.operations?.customers?.typicalCustomer ?? null,
      countries: businessInfo?.operations?.customers?.countries ?? null,
      quantity: businessInfo?.operations?.customers?.quantity ?? null,
      industries: businessInfo?.operations?.customers?.industries ?? null,
    };
  }
}

export interface CustomerBusinessOperationsFundsSourcePatchRequest
  extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.businessOperationsFundsSource;

  readonly thirdPartyBeneficiaries: boolean | null;
  readonly usdVolume: string | null;
}

export interface CustomerBusinessOperationsFundsSource {
  readonly thirdPartyBeneficiaries: boolean | null;
  readonly usdVolume: string | null;
}

export class CustomerBusinessOperationsFundsSource {
  public static fromCustomer(customer: Customer): CustomerBusinessOperationsFundsSource {
    const { businessInfo } = customer;

    return {
      thirdPartyBeneficiaries:
        businessInfo?.operations?.fundsSource?.thirdPartyBeneficiaries ?? null,
      usdVolume: businessInfo?.operations?.fundsSource?.usdVolume ?? null,
    };
  }
}

export interface CustomerUserPersonalInfoPatchRequest extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.userPersonalInfo;

  readonly firstName: string | null;
  readonly lastName: string | null;
}

export interface CustomerOrganizationLegalRepresentative {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phoneNumber: string | null;
  readonly document: string | null;
}

export class CustomerOrganizationLegalRepresentative {
  public static fromCustomer(customer: Customer): CustomerOrganizationLegalRepresentative {
    const { businessInfo } = customer;

    return {
      firstName: businessInfo?.organization?.legalRepresentative?.firstName ?? null,
      lastName: businessInfo?.organization?.legalRepresentative?.lastName ?? null,
      email: businessInfo?.organization?.legalRepresentative?.email ?? null,
      phoneNumber: businessInfo?.organization?.legalRepresentative?.phoneNumber ?? null,
      document: businessInfo?.organization?.legalRepresentative?.document ?? null,
    };
  }
}

export interface CustomerOrganizationLegalRepresentativePatchRequest
  extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.organizationLegalRepresentative;

  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phoneNumber: string | null;
}

export interface CustomerOrganizationContactPatchRequest extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.organizationContact;

  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phoneNumber: string | null;
  readonly position: string | null;
}

export interface CustomerOrganizationContact {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phoneNumber: string | null;
  readonly position: string | null;
}

export class CustomerOrganizationContact {
  public static fromCustomer(customer: Customer): CustomerOrganizationContact {
    const { businessInfo } = customer;

    return {
      firstName: businessInfo?.organization?.contact?.firstName ?? null ?? customer.firstName,
      lastName: businessInfo?.organization?.contact?.lastName ?? null ?? customer.lastName,
      email: businessInfo?.organization?.contact?.email ?? null ?? customer.email,
      phoneNumber: businessInfo?.organization?.contact?.phoneNumber ?? null,
      position: businessInfo?.organization?.contact?.position ?? null,
    };
  }
}

export interface CustomerSocialNetworkPatchRequest extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.socialNetwork;

  readonly website: string | null;
  readonly socialMediaNetworks: readonly string[] | null;
}

export interface CustomerSocialNetwork {
  readonly website: string | null;
  readonly socialMediaNetworks: readonly string[] | null;
}

export class CustomerSocialNetwork {
  public static fromCustomer(customer: Customer): CustomerSocialNetwork {
    const { businessInfo } = customer;

    return {
      website: businessInfo?.socialNetwork?.website ?? null,
      socialMediaNetworks: businessInfo?.socialNetwork?.socialMediaNetworks ?? null,
    };
  }
}

export interface CustomerOrganizationOwnersPatchRequest extends CustomerProfilePatchRequestBase {
  readonly type: CustomerSliceType.organizationOwners;

  readonly owners: readonly Owner[] | null;
}

export interface CustomerOrganizationOwners {
  readonly owners: readonly Owner[] | null;
}

export class CustomerOrganizationOwners {
  public static fromCustomer(customer: Customer): CustomerOrganizationOwners {
    const { businessInfo } = customer;

    return {
      owners: businessInfo?.organization?.owners ?? null,
    };
  }
}

export type CustomerPatchRequest =
  | CustomerCompanyDetailsPatchRequest
  | CustomerBusinessOperationsProfilePatchRequest
  | CustomerBusinessOperationsCustomersPatchRequest
  | CustomerBusinessOperationsFundsSourcePatchRequest
  | CustomerUserPersonalInfoPatchRequest
  | CustomerOrganizationLegalRepresentativePatchRequest
  | CustomerOrganizationContactPatchRequest
  | CustomerSocialNetworkPatchRequest
  | CustomerOrganizationOwnersPatchRequest;

type ExistingCustomer = Omit<Customer, 'businessInfo'> & {
  readonly businessInfo: BusinessInfo;
};

const isExistingCustomer = (customer: Customer): customer is ExistingCustomer =>
  customer.businessInfo !== null;

const patchBusinessOperationsProfile = (
  customer: ExistingCustomer,
  patchRequest: CustomerBusinessOperationsProfilePatchRequest,
): BusinessInfo => {
  const { type: _, ...fields } = patchRequest;
  const { businessInfo } = customer;
  const { operations = BusinessOperations.empty() } = withNullsRemoved(businessInfo);
  const { profile = OperationsProfile.empty() } = withNullsRemoved(operations);

  return {
    ...customer.businessInfo,
    operations: {
      ...operations,
      profile: copyWith(profile, fields),
    },
  };
};

const patchBusinessOperationsCustomers = (
  customer: ExistingCustomer,
  patchRequest: CustomerBusinessOperationsCustomersPatchRequest,
): BusinessInfo => {
  const { type: _, ...fields } = patchRequest;
  const { businessInfo } = customer;
  const { operations = BusinessOperations.empty() } = withNullsRemoved(businessInfo);
  const { customers = OperationsCustomers.empty() } = withNullsRemoved(operations);

  return {
    ...customer.businessInfo,
    operations: {
      ...operations,
      customers: copyWith(customers, withNullsRemoved(fields)),
    },
  };
};

const patchBusinessOperationsFundsSource = (
  customer: ExistingCustomer,
  patchRequest: CustomerBusinessOperationsFundsSourcePatchRequest,
): BusinessInfo => {
  const { type: _, ...fields } = patchRequest;
  const { businessInfo } = customer;
  const { operations = BusinessOperations.empty() } = withNullsRemoved(businessInfo);
  const { fundsSource = OperationsFundsSource.empty() } = withNullsRemoved(operations);

  return {
    ...customer.businessInfo,
    operations: {
      ...operations,
      fundsSource: copyWith(fundsSource, withNullsRemoved(fields)),
    },
  };
};

const patchOrganizationLegalRepresentative = (
  customer: ExistingCustomer,
  patchRequest: CustomerOrganizationLegalRepresentativePatchRequest,
): BusinessInfo => {
  const { type: _, ...fields } = patchRequest;
  const { businessInfo } = customer;
  const { organization = Organization.empty() } = withNullsRemoved(businessInfo);
  const { legalRepresentative = LegalRepresentative.empty() } = withNullsRemoved(organization);

  return {
    ...customer.businessInfo,
    organization: {
      ...organization,
      legalRepresentative: copyWith(legalRepresentative, withNullsRemoved(fields)),
    },
  };
};

const patchOrganizationContact = (
  customer: ExistingCustomer,
  patchRequest: CustomerOrganizationContactPatchRequest,
): BusinessInfo => {
  const { type: _, ...fields } = patchRequest;
  const { businessInfo } = customer;
  const { organization = Organization.empty() } = withNullsRemoved(businessInfo);
  const { contact = BusinessContact.empty() } = withNullsRemoved(organization);

  return {
    ...customer.businessInfo,
    organization: {
      ...organization,
      contact: copyWith(contact, withNullsRemoved(fields)),
    },
  };
};

// FIXME: this will not work
const patchOrganizationOwners = (
  customer: ExistingCustomer,
  patchRequest: CustomerOrganizationOwnersPatchRequest,
): BusinessInfo => {
  const { type: _, ...fields } = patchRequest;
  const { businessInfo } = customer;
  const { organization = Organization.empty() } = withNullsRemoved(businessInfo);
  const { owners } = withNullsRemoved(fields);
  if (!owners) {
    return customer.businessInfo;
  }

  return {
    ...customer.businessInfo,
    organization: {
      ...organization,
      owners: owners /* The entire list is sent by the change event */,
    },
  };
};

export const patchOwnerDocument = (
  businessInfo: BusinessInfo,
  ownerId: string | undefined,
  fileName: string | null,
): BusinessInfo => {
  const { organization } = businessInfo;

  const legalRepresentative = organization?.legalRepresentative ?? LegalRepresentative.empty();
  const owners = organization?.owners ?? [];
  const contact = organization?.contact ?? null;

  return {
    ...businessInfo,
    organization: {
      contact: contact,
      owners: owners.map((owner: Owner): Owner => {
        if ('id' in owner && typeof owner.id === 'string' && owner.id !== ownerId) {
          return owner;
        } else {
          return { ...owner, document: fileName };
        }
      }),
      legalRepresentative: legalRepresentative,
    },
  };
};

export const patchLegalRepresentativeDocument = (
  businessInfo: BusinessInfo,
  fileName: string | null,
): BusinessInfo => {
  const { organization } = businessInfo;

  const legalRepresentative = organization?.legalRepresentative ?? LegalRepresentative.empty();
  const owners = organization?.owners ?? [];
  const contact = organization?.contact ?? null;

  return {
    ...businessInfo,
    organization: {
      contact: contact,
      owners: owners,
      legalRepresentative: {
        ...legalRepresentative,
        document: fileName,
      },
    },
  };
};

const patchSocialNetwork = (
  customer: ExistingCustomer,
  patchRequest: CustomerSocialNetworkPatchRequest,
): BusinessInfo => {
  const { type: _, ...fields } = patchRequest;
  const { businessInfo } = customer;
  const { socialNetwork = SocialNetwork.empty() } = withNullsRemoved(businessInfo);

  return {
    ...customer.businessInfo,
    socialNetwork: {
      socialMediaNetworks: fields.socialMediaNetworks ?? socialNetwork.socialMediaNetworks,
      website: fields.website ?? socialNetwork.website,
    },
  };
};

export const patchBusinessInfo = (
  customer: Customer,
  patchRequest: CustomerPatchRequest,
): BusinessInfo => {
  const { type, ...patchedFields } = patchRequest;

  if (!isExistingCustomer(customer)) {
    throw new Error('Cannot patch company details without business info');
  }

  switch (type) {
    case CustomerSliceType.companyDetails:
      return {
        ...customer.businessInfo,
        ...patchedFields,
      };

    case CustomerSliceType.businessOperationsProfile:
      return patchBusinessOperationsProfile(customer, patchRequest);

    case CustomerSliceType.businessOperationsCustomers:
      return patchBusinessOperationsCustomers(customer, patchRequest);

    case CustomerSliceType.businessOperationsFundsSource:
      return patchBusinessOperationsFundsSource(customer, patchRequest);

    case CustomerSliceType.userPersonalInfo:
      throw new Error('Cannot patch user personal info');

    case CustomerSliceType.organizationLegalRepresentative:
      return patchOrganizationLegalRepresentative(customer, patchRequest);

    case CustomerSliceType.organizationContact:
      return patchOrganizationContact(customer, patchRequest);

    case CustomerSliceType.socialNetwork:
      return patchSocialNetwork(customer, patchRequest);

    case CustomerSliceType.organizationOwners:
      return patchOrganizationOwners(customer, patchRequest);

    default:
      throw new Error(`Unknown patch request type: ${type}`);
  }
};

export interface OperationsProfile {
  readonly industry: IndustryType | null;
  readonly subcategories: readonly string[] | null;
  readonly services: string | null;
  readonly countries: readonly string[] | null;
  readonly isFinancialInstitution: boolean | null;
  // FIXME: this should probably not be here
  readonly hasBrazilLicenseRequirement: boolean | null;
}

export class OperationsProfile {
  public static empty(): OperationsProfile {
    return {
      industry: null as any, // Replace `null as any` with actual default value or handle appropriately
      subcategories: null,
      services: null,
      countries: null,
      isFinancialInstitution: null,
      hasBrazilLicenseRequirement: null,
    };
  }
}

export interface OperationsCustomers {
  readonly customerType: readonly BeneficiaryType[] | null;
  readonly typicalCustomer: string | null;
  readonly countries: readonly string[] | null;
  readonly quantity: number | null;
  readonly industries: readonly CustomerIndustryType[] | null;
}

export class OperationsCustomers {
  public static empty(): OperationsCustomers {
    return {
      customerType: null,
      typicalCustomer: null,
      countries: null,
      quantity: null,
      industries: null,
    };
  }
}

export interface OperationsFundsSource {
  readonly thirdPartyBeneficiaries: boolean | null;
  readonly usdVolume: string | null;
}

export class OperationsFundsSource {
  public static empty(): OperationsFundsSource {
    return {
      thirdPartyBeneficiaries: null,
      usdVolume: null,
    };
  }
}

export interface SocialNetwork {
  readonly website: string;
  readonly socialMediaNetworks: readonly string[];
}

export class SocialNetwork {
  public static empty(): SocialNetwork {
    return {
      website: '',
      socialMediaNetworks: [],
    };
  }
}

export interface BusinessContact {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly position: string;
}

export class BusinessContact {
  public static empty(): BusinessContact {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      position: '',
    };
  }
}

export class BusinessInfo {
  public static empty(): BusinessInfo {
    return {
      name: '',
      country: '',
      commercialName: null,
      legalEntityName: null,
      entityType: null,
      taxId: null,
      operationStartDate: null,
      address: null,
      operations: BusinessOperations.empty(),
      socialNetwork: SocialNetwork.empty(),
      organization: Organization.empty(),
      documents: CustomerDocuments.empty(),
    };
  }
}

export interface LegalRepresentative {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly document: string | null;
}

export class LegalRepresentative {
  public static empty(): LegalRepresentative {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      document: '',
    };
  }
}

export interface Organization {
  readonly legalRepresentative: LegalRepresentative | null;
  readonly contact: BusinessContact | null;
  readonly owners: readonly Owner[] | null;
}

export class Organization {
  public static empty(): Organization {
    return {
      legalRepresentative: null,
      contact: null,
      owners: null,
    };
  }
}

export interface BusinessOperations {
  readonly profile: OperationsProfile | null;
  readonly customers: OperationsCustomers | null;
  readonly fundsSource: OperationsFundsSource | null;
}

export class BusinessOperations {
  public static empty(): BusinessOperations {
    return {
      profile: OperationsProfile.empty(),
      customers: OperationsCustomers.empty(),
      fundsSource: OperationsFundsSource.empty(),
    };
  }
}

export interface CustomerDocuments {
  readonly incorporation: string | null;
  readonly cnpjRegistration: string | null;
  readonly powerOfAttorney: string | null;
  readonly bankAccountStatement: string | null;
}

export enum CustomerDocumentType {
  incorporation = 'incorporation',
  cnpjRegistration = 'cnpjRegistration',
  powerOfAttorney = 'powerOfAttorney',
  bankAccountStatement = 'bankAccountStatement',
}

export class CustomerDocuments {
  public static empty(): CustomerDocuments {
    return {
      incorporation: '',
      cnpjRegistration: '',
      powerOfAttorney: '',
      bankAccountStatement: '',
    };
  }
}

export interface BusinessInfo {
  readonly name: string;
  readonly country: string;

  readonly commercialName: string | null;
  readonly legalEntityName: string | null;
  readonly entityType: EntityType | null;
  readonly taxId: string | null;
  // TODO: this is a java.time.LocalDate
  readonly operationStartDate: string | null;
  readonly address: BusinessAddress | null;
  readonly operations: BusinessOperations | null;
  readonly socialNetwork: SocialNetwork | null;
  readonly organization: Organization | null;
  readonly documents: CustomerDocuments | null;
}

export interface BusinessDomainAddress {
  readonly address: string;
  readonly city: string;
  readonly geolocation: string;
  readonly state: string;
  readonly streetOne: string;
  readonly streetTwo: string;
  readonly zipcode: string;
}

export interface BusinessDomainContact {
  readonly firstName: string;
  readonly lastName: string;
  readonly dateOfBirth: string;
  readonly placeOfBirth: string;
  readonly citizenship: string;
  readonly idNumber: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly profession: string;
  readonly maritalStatus: string;
  readonly mothersName: string;
  readonly gender: string;
  readonly address: BusinessDomainAddress;
  readonly industry: string | null;
  readonly incomeSource: string | null;
  readonly annualIncome: number | null;
  readonly occupation: string | null;
  readonly wealthSource: string | null;
}

export interface BusinessDomain {
  readonly name: string;
  readonly idNumber: string;
  readonly address: BusinessDomainAddress;
  readonly contacts: readonly BusinessDomainContact[];
  readonly dateOfIncorporation: string;
  readonly email: string;
  readonly legalStructure: string;
  readonly phoneNumber: string;
  readonly website: string;
}

export interface Customer {
  readonly userId: string;
  readonly keycloakId: string;

  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  // @deprecated should use `business`
  readonly businessInfo: BusinessInfo | null;
  readonly business: BusinessDomain;

  readonly benefits: string | null;
  readonly emailVerified: boolean;

  readonly integratorId: string | null;
  readonly beneficiaryId: string | null;
  readonly kycVerified: boolean | null;

  readonly totpEnabled: boolean | null;
  readonly permissions: readonly Permission[];
  readonly group: string;

  readonly passwordUpdatable: boolean;
}

export class Customer {
  public static empty(): Customer {
    return {
      userId: '',
      keycloakId: '',
      firstName: '',
      lastName: '',
      email: '',
      businessInfo: null,
      business: {
        name: '',
        idNumber: '',
        address: {
          address: '',
          city: '',
          geolocation: '',
          state: '',
          streetOne: '',
          streetTwo: '',
          zipcode: '',
        },
        contacts: [],
        dateOfIncorporation: '',
        email: '',
        legalStructure: '',
        phoneNumber: '',
        website: '',
      },
      benefits: null,
      emailVerified: false,
      integratorId: null,
      beneficiaryId: null,
      kycVerified: false,
      totpEnabled: false,
      permissions: [],
      group: '',
      passwordUpdatable: false,
    };
  }
}

export const getCustomerSlice = (customer: Customer | undefined, key: string): any => {
  if (customer) {
    if (isCustomerSliceType(key)) {
      return extractCustomerSlice(key, customer);
    } else if (key === 'documents') {
      const { documents = CustomerDocuments.empty() } = customer.businessInfo ?? {};
      if (!documents) {
        return undefined;
      }
      return documents;
    } else {
      console.debug(`Unknown patch request type: ${key}`);
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getCustomerTaxId = (customer: Customer | undefined): string => {
  return (customer?.business?.idNumber ?? '').replace(/[^a-zA-Z0-9]/g, '');
};

export * from '@/types/customer.ext';
